// RoleBasedHeader.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

import {
  Bell, User, LogOut, Shield, Sun, Moon, Menu, Lock,
  ChevronDown, Check, X, ExternalLink, RefreshCw, Trash2,
  Settings, Search
} from 'lucide-react';

interface RoleBasedHeaderProps {
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  isMobile?: boolean;
  sidebarWidth?: string;
  isSidebarCollapsed?: boolean;
}

const RoleBasedHeader: React.FC<RoleBasedHeaderProps> = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isMobile = false,
  sidebarWidth = 'w-64',
  isSidebarCollapsed = false
}) => {
  const { user, logout } = useAuth();
  const { t, language } = useLocalization();
  const { actualTheme, toggleTheme } = useTheme();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    showNotificationDropdown,
    setShowNotificationDropdown
  } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  if (!user) return null;

  // --- PORTAL POSITION ---
  const [notifPos, setNotifPos] = useState<{ top: number; right: number } | null>(null);
  const bellBtnRef = useRef<HTMLButtonElement>(null);

  const computeNotifPos = useCallback(() => {
    const rect = bellBtnRef.current?.getBoundingClientRect();
    if (!rect) return;
    setNotifPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right
    });
  }, []);

  // Reposition quand ouvert + scroll/resize
  useEffect(() => {
    if (!showNotificationDropdown) return;

    computeNotifPos();

    const onScroll = () => computeNotifPos();
    const onResize = () => computeNotifPos();

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [showNotificationDropdown, computeNotifPos]);

  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'merchant_admin': return { label: 'Admin', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'merchant_finance': return { label: 'Finance', color: 'bg-teal-50 text-teal-700 border-teal-200' };
      case 'merchant_support': return { label: 'Support', color: 'bg-amber-50 text-amber-700 border-amber-200' };
      default: return { label: 'User', color: 'bg-gray-50 text-gray-700 border-gray-200' };
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'merchant_admin': return t('role.merchantAdmin');
      case 'merchant_finance': return t('role.finance');
      case 'merchant_support': return t('role.support');
      default: return t('role.user');
    }
  };

  const roleBadge = getRoleBadge(user.role ?? 'user');

  const formatRelativeTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return t("header.justNow");
      if (diffMins < 60) return language === 'fr' ? `Il y a ${diffMins} min` : `${diffMins} min ago`;
      if (diffHours < 24) return language === 'fr' ? `Il y a ${diffHours} h` : `${diffHours} h ago`;
      if (diffDays < 7) return language === 'fr' ? `Il y a ${diffDays} j` : `${diffDays} d ago`;

      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return t('header.unknownDate');
    }
  };

  const getStatusDot = (status?: string) => {
    switch (status) {
      case 'SUCCESS': return 'bg-emerald-500';
      case 'REJECTED': return 'bg-rose-500';
      case 'PENDING': return 'bg-amber-500';
      default: return 'bg-gray-400';
    }
  };

  const handleViewTransfer = (reference?: string) => {
    if (!reference) return;
    setShowNotificationDropdown(false);
    navigate(`/transfer-details/${encodeURIComponent(reference)}`);
  };

  const handleViewAllTransfers = () => {
    setShowNotificationDropdown(false);
    setTimeout(() => navigate('/transfers'), 150);
  };

  // Get page title from current location
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return t('nav.dashboard');
    if (path.includes('/transfers')) return t('nav.transfers');
    if (path.includes('/profile')) return t('nav.profile');
    if (path.includes('/settings')) return t('nav.settings');
    if (path.includes('/team')) return t('nav.team');
    if (path.includes('/tickets')) return t('nav.tickets');
    return t('nav.dashboard');
  };

  // Notification dropdown content (shared between mobile/desktop)
  const NotificationDropdownContent = ({ isMobileView = false }: { isMobileView?: boolean }) => (
    <div className={`
      ${isMobileView
        ? 'fixed inset-x-3 top-[60px] z-[9999] w-auto max-h-[70vh]'
        : 'fixed z-[99999] w-[380px] max-h-[480px]'
      }
      flex flex-col bg-white border border-gray-200 rounded-xl shadow-xl
    `}
      style={!isMobileView && notifPos ? { top: notifPos.top, right: notifPos.right } : undefined}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t("header.notifications")}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {unreadCount} {t("header.unread")}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                title={t("header.markAllRead")}
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAllNotifications}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t("header.clearAll")}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('refreshData'))}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title={t("common.refresh")}
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            {isMobileView && (
              <button
                onClick={() => setShowNotificationDropdown(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notification list */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="py-10 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
              <Bell className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">{t("header.noNotifications")}</p>
            <p className="text-xs text-gray-400 mt-1">{t("header.notifAppearHere")}</p>
          </div>
        ) : (
          <div>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !notification.read ? 'bg-emerald-50/40' : ''
                }`}
                onClick={() => {
                  markAsRead(notification.id);
                  if (notification.reference) handleViewTransfer(notification.reference);
                }}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${getStatusDot(notification.status)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{notification.title}</h4>
                      {!notification.read && (
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">{notification.message}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-gray-400">{formatRelativeTime(notification.timestamp)}</span>
                      {notification.reference && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTransfer(notification.reference);
                          }}
                          className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5"
                        >
                          {t("transfers.viewDetails")}
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(notification.id);
                    }}
                    className="mt-0.5 p-1 text-gray-300 hover:text-red-500 rounded transition-colors flex-shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-gray-100">
        <button
          onClick={handleViewAllTransfers}
          className="w-full py-2 text-sm text-center text-emerald-600 hover:text-emerald-700 hover:bg-indigo-50 rounded-lg transition-colors font-medium"
        >
          {t("transfers.viewAll")}
        </button>
      </div>
    </div>
  );

  // MOBILE
  if (isMobile) {
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 w-full">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMobileMenuOpen?.(!isMobileMenuOpen)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="h-8 w-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm">
            <img src="/LOGO_MOKO_A_fd_vert.png" alt="MOKO" className="h-4 w-auto" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={actualTheme === 'light' ? t("header.switchDark") : t("header.switchLight")}
          >
            {actualTheme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>

          <div className="relative">
            <button
              onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
              className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotificationDropdown && (
              <>
                <div
                  className="fixed inset-0 z-[9998] bg-black/10"
                  onClick={() => setShowNotificationDropdown(false)}
                />
                <NotificationDropdownContent isMobileView={true} />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // DESKTOP
  return (
    <div className={`
      fixed top-9 left-0 right-0
      flex items-center justify-between px-6 h-16
      bg-white/95 backdrop-blur-md border-b border-gray-200/80
      z-30 transition-all duration-300 ease-out
      ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}
    `}>
      {/* Left: page title */}
      <div className="hidden md:flex items-center gap-3 pl-4">
        <h1 className="text-base font-semibold text-gray-900">{getPageTitle()}</h1>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title={actualTheme === 'light' ? t("header.switchDark") : t("header.switchLight")}
        >
          {actualTheme === 'light'
            ? <Moon className="h-[18px] w-[18px]" />
            : <Sun className="h-[18px] w-[18px] text-amber-500" />
          }
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            ref={bellBtnRef}
            onClick={() => {
              computeNotifPos();
              setShowNotificationDropdown(!showNotificationDropdown);
            }}
            className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Bell className="h-[18px] w-[18px]" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {showNotificationDropdown && notifPos && createPortal(
            <>
              <div
                className="fixed inset-0 z-[99998]"
                onMouseDown={() => setShowNotificationDropdown(false)}
              />
              <NotificationDropdownContent />
            </>,
            document.body
          )}
        </div>

        {/* Separator */}
        <div className="h-6 w-px bg-gray-200 mx-1" />

        {/* User Menu */}
        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm shadow-emerald-500/20">
              <span className="text-white font-semibold text-xs">
                {user.avatar ?? user.name?.charAt(0).toUpperCase() ?? "U"}
              </span>
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                {user.username ?? t("common.user")}
              </p>
              <p className="text-xs text-gray-500 truncate max-w-[140px]">
                {user.email || getRoleLabel(user.role ?? "user")}
              </p>
            </div>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showUserDropdown && (
            <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-[9999] overflow-hidden">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow shadow-emerald-500/20">
                    <span className="text-white font-semibold text-sm">
                      {user.avatar ?? user.name?.charAt(0).toUpperCase() ?? "U"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${roleBadge.color}`}>
                      {roleBadge.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={() => { navigate('/profile'); setShowUserDropdown(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <User className="h-4 w-4 text-gray-400" />
                  {t('nav.profile')}
                </button>
                <button
                  onClick={() => { navigate('/settings'); setShowUserDropdown(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="h-4 w-4 text-gray-400" />
                  {t("nav.settings")}
                </button>
                <button
                  onClick={() => { navigate('/settings'); setShowUserDropdown(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Shield className="h-4 w-4 text-gray-400" />
                  {t("common.security")}
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 p-2">
                <button
                  onClick={() => {
                    logout?.();
                    navigate('/login');
                    setShowUserDropdown(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                >
                  <LogOut className="h-4 w-4" />
                  {t('common.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleBasedHeader;
